import { Form, Formik } from "formik";
import { inject } from "mobx-react";
import { Component } from "react";
import { FormattedMessage, type IntlShape } from "react-intl";
import { compose } from "nvent-web/utils/compose";
import * as Yup from "yup";
import { TextField } from "nvent-web/components/form/TextField";
import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import { RequiredFieldLabel } from "nvent-web/components/RequiredFieldLabel/RequiredFieldLabel";
import SubmitCancelButtons from "nvent-web/components/SubmitCancelButtons";
import Api from "nvent-web/services/Api";
import * as logger from "nvent-web/services/logger";
import { NotificationsStore } from "nvent-web/stores/Notifications";
import { type Address } from "nvent-web/types/Address";

import style from "./EditLevelForm.module.scss";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

type EditLevelFormParams = {
  projectId: string;
  id: string;
};

type EditLevelFormInnerProps = EditLevelFormParams &
  WithRouterProps & {
    intl: IntlShape;
    api: Api;
    notifications: NotificationsStore;
  };

type EditLevelFormValues = Pick<
  Address,
  "streetAddress" | "postalCode" | "locality"
>;
class EditLevelFormInner extends Component<EditLevelFormInnerProps> {
  schema = Yup.object({
    streetAddress: Yup.string().required(),
    postalCode: Yup.string(),
    locality: Yup.string().required(),
  });

  state = {
    address: {
      streetAddress: "",
      postalCode: "",
      locality: "",
    },
    isLevelFetching: false,
    isLevelSubmitting: false,
  };

  async componentDidMount() {
    try {
      this.setState({ isLevelFetching: true });
      const { data: level } = await this.props.api.levels.getOne({
        projectId: Number(this.props.params.projectId),
        levelId: Number(this.props.params.id),
      });
      this.setState({
        address: {
          streetAddress: level.address.streetAddress,
          postalCode: level.address.postalCode,
          locality: level.address.locality,
        },
      });
    } catch (err) {
      logger.error(err);
      this.props.notifications.createError(
        <FormattedMessage id="error.getLevelFailed" />
      );
    } finally {
      this.setState({ isLevelFetching: false });
    }
  }

  render() {
    const { isLevelFetching, isLevelSubmitting, address } = this.state;

    if (isLevelFetching) {
      return <LoadingSection />;
    }

    return (
      <>
        <Formik
          validationSchema={this.schema}
          initialValues={address}
          onSubmit={this.handleSubmit}
          enableReinitialize
        >
          {() => (
            <Form noValidate={true} className={style.form}>
              <div className={style.doubleColumn}>
                <TextField
                  required
                  type="text"
                  name="streetAddress"
                  label={<FormattedMessage id="form.address" />}
                />
              </div>

              <TextField
                type="text"
                name="postalCode"
                label={<FormattedMessage id="form.zipCode" />}
              />
              <TextField
                required
                type="text"
                name="locality"
                label={<FormattedMessage id="form.locality" />}
              />

              <RequiredFieldLabel />

              <div className={style.actions}>
                <SubmitCancelButtons
                  disabled={isLevelSubmitting}
                  loading={isLevelSubmitting}
                  onCancel={() => this.props.navigate(-1)}
                  submitLabel={<FormattedMessage id="actions.save" />}
                />
              </div>
            </Form>
          )}
        </Formik>
      </>
    );
  }

  private handleSubmit = async (formValues: EditLevelFormValues) => {
    try {
      this.setState({ isLevelSubmitting: true });
      const projectId = Number(this.props.params.projectId);
      const levelId = Number(this.props.params.id);

      await this.props.api.levels.edit({
        projectId,
        levelId,
        data: { addressAttributes: formValues },
      });
      this.props.navigate(`/projects/${projectId}`);
    } catch (err) {
      logger.error(err);
      this.props.notifications.createError(
        <FormattedMessage id="error.editLevelFailed" />
      );
    } finally {
      this.setState({ isLevelSubmitting: false });
    }
  };
}

export const EditLevelForm = compose(
  inject("api", "notifications"),
  withRouter
)(EditLevelFormInner);
